import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, IconButton } from "@telegram-apps/telegram-ui";
import { CurrenciesModal } from "../CurrenciesModal/CurrenciesModal";
import { AirdropModal } from "../AirdropModal/AirdropModal";
import { RechargeModal } from "../RechargeModal/RechargeModal";
import { UserMax } from "../../handlers/types";
import { formatTimeFromMs } from "../../utils/timeUtils";
import { formatNumber, formatManaRate } from "../../utils/formatters";
import { t } from "../../utils/i18n/index";
import "./MainMenuHeader.css";

interface MainMenuHeaderProps {
  userData: UserMax | null;
  onSettingsClick: () => void;
  onUserDataRefresh?: () => void;
}

export function MainMenuHeader({
  userData,
  onSettingsClick,
  onUserDataRefresh,
}: MainMenuHeaderProps): JSX.Element {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUSDTModalOpen, setIsUSDTModalOpen] = useState(false);
  const [isRechargeModalOpen, setIsRechargeModalOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  // Calculate time remaining and update every second
  useEffect(() => {
    if (!userData?.mana?.isGenerating) {
      setTimeRemaining(0);
      return;
    }

    // Set initial time from the nextRechargeNeeded duration
    setTimeRemaining(userData.mana.nextRechargeNeeded);

    // Set up interval to countdown every second
    const interval = setInterval(() => {
      setTimeRemaining((prev) => Math.max(0, prev - 1000));
    }, 1000);

    // Clean up interval on unmount
    return () => clearInterval(interval);
  }, [userData?.mana?.nextRechargeNeeded, userData?.mana?.isGenerating]);

  const handleInfoClick = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleCoinsClick = () => {
    navigate("/shop");
  };

  const handleUSDTClick = () => {
    setIsUSDTModalOpen(true);
  };

  const handleCloseUSDTModal = () => {
    setIsUSDTModalOpen(false);
  };

  const handleRechargeClick = () => {
    setIsRechargeModalOpen(true);
  };

  const handleCloseRechargeModal = () => {
    setIsRechargeModalOpen(false);
  };

  const handleRechargeSuccess = () => {
    // Refresh user data to show updated mana information and balance
    if (onUserDataRefresh) {
      // Force a hard refresh to get the latest balance from server
      onUserDataRefresh();
    }
    setIsRechargeModalOpen(false);
  };

  // Format mana rate with appropriate time unit
  const { rate, unit } = formatManaRate(userData?.mana?.generationRate || 0);

  return (
    <div className="main-menu__header">
      <div className="main-menu__stats-row">
        {/* Coins */}
        <div className="main-menu__stat-item">
          <img
            src="/assets/img/ui/coin.webp"
            alt={t("header.coins")}
            className="main-menu__stat-icon"
          />
          <div className="main-menu__stat-value-container">
            <div className="main-menu__stat-value">
              {formatNumber(userData?.balance || 0)}
            </div>
            <div className="main-menu__stat-label">{t("header.coins")}</div>
          </div>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={handleCoinsClick}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
              stroke="#84919E"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* USDT */}
        <div className="main-menu__stat-item">
          <img
            src="/assets/img/ui/USDT.webp"
            alt="USDT"
            className="main-menu__stat-icon"
          />
          <div className="main-menu__stat-value-container">
            <div className="main-menu__stat-value">
              {formatNumber(userData?.usdt || 0)}
            </div>
            <div className="main-menu__stat-label">USDT</div>
          </div>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={handleUSDTClick}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M2.33938 14.5895C1.44846 11.2533 2.31164 7.54618 4.92893 4.92889C8.83418 1.02364 15.1658 1.02364 19.0711 4.92889C22.9763 8.83413 22.9763 15.1658 19.0711 19.071C16.4538 21.6883 12.7466 22.5515 9.41045 21.6606M15.0001 15.0001V9.00006M15.0001 9.00006H9.00014M15.0001 9.00006L4.99995 19"
              stroke="#84919E"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* $EVL */}
        <div className="main-menu__stat-item">
          <img
            src="/assets/img/ui/EVL.webp"
            alt="$EVL"
            className="main-menu__stat-icon"
          />
          <div className="main-menu__stat-value-container">
            <div className="main-menu__stat-value">
              {formatNumber(userData?.evl || 0)}
            </div>
            <div className="main-menu__stat-label">$EVL</div>
          </div>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={handleInfoClick}
            style={{ cursor: "pointer" }}
          >
            <path
              d="M11 15V11M11 7H11.01M21 11C21 16.5228 16.5228 21 11 21C5.47715 21 1 16.5228 1 11C1 5.47715 5.47715 1 11 1C16.5228 1 21 5.47715 21 11Z"
              stroke="#84919E"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>

      <div className="main-menu__stats-row">
        {/* Mana */}
        <div className="main-menu__stat-item main-menu__stat-item--mana">
          <div className="main-menu__mana-status-container">
            <img
              src="/assets/img/ui/mana.webp"
              alt={t("common.mana")}
              className="main-menu__stat-icon"
            />
            <span className="main-menu__stat-value">
              <div>
                <span className="main-menu__stat-value-number">{rate}</span>{" "}
                {t("header.mana_rate", { unit: t(`header.time_unit.${unit}`) })}
              </div>
              <div
                className={`main-menu__mana-status ${
                  !userData?.mana?.isGenerating
                    ? "main-menu__mana-status--paused"
                    : ""
                }`}
              >
                {userData?.mana?.isGenerating
                  ? t("header.charge_time", {
                      time: formatTimeFromMs(timeRemaining),
                    })
                  : t("header.paused")}
              </div>
            </span>
          </div>

          {/* Only show recharge button when mana is NOT generating */}
          {!userData?.mana?.isGenerating && (
            <div className="main-menu__controls">
              <Button
                size="s"
                className="main-menu__auto-recharge-button main-menu__auto-recharge-button--paused"
                onClick={handleRechargeClick}
              >
                {t("header.recharge")}
              </Button>
            </div>
          )}
        </div>

        <div className="main-menu__stat-item">
          {/* Settings */}
          <IconButton
            className="main-menu__settings-button"
            onClick={onSettingsClick}
            size="s"
          >
            <svg
              width="20"
              height="22"
              viewBox="0 0 20 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="main-menu__settings-icon"
            >
              <path
                d="M7.39504 18.3711L7.97949 19.6856C8.15323 20.0768 8.43676 20.4093 8.79571 20.6426C9.15466 20.8759 9.5736 21.0001 10.0017 21C10.4298 21.0001 10.8488 20.8759 11.2077 20.6426C11.5667 20.4093 11.8502 20.0768 12.0239 19.6856L12.6084 18.3711C12.8164 17.9047 13.1664 17.5159 13.6084 17.26C14.0532 17.0034 14.5677 16.8941 15.0784 16.9478L16.5084 17.1C16.934 17.145 17.3636 17.0656 17.7451 16.8713C18.1265 16.6771 18.4434 16.3763 18.6573 16.0056C18.8714 15.635 18.9735 15.2103 18.951 14.7829C18.9285 14.3555 18.7825 13.9438 18.5306 13.5978L17.6839 12.4344C17.3825 12.0171 17.2214 11.5148 17.2239 11C17.2238 10.4866 17.3864 9.98635 17.6884 9.57111L18.535 8.40778C18.7869 8.06175 18.933 7.65007 18.9554 7.22267C18.9779 6.79528 18.8759 6.37054 18.6617 6C18.4478 5.62923 18.1309 5.32849 17.7495 5.13423C17.3681 4.93997 16.9385 4.86053 16.5128 4.90556L15.0828 5.05778C14.5722 5.11141 14.0576 5.00212 13.6128 4.74556C13.1699 4.48825 12.8199 4.09736 12.6128 3.62889L12.0239 2.31444C11.8502 1.92317 11.5667 1.59072 11.2077 1.3574C10.8488 1.12408 10.4298 0.99993 10.0017 1C9.5736 0.99993 9.15466 1.12408 8.79571 1.3574C8.43676 1.59072 8.15323 1.92317 7.97949 2.31444L7.39504 3.62889C7.18797 4.09736 6.83792 4.48825 6.39504 4.74556C5.95026 5.00212 5.43571 5.11141 4.92504 5.05778L3.4906 4.90556C3.06493 4.86053 2.63534 4.93997 2.25391 5.13423C1.87249 5.32849 1.55561 5.62923 1.34171 6C1.12753 6.37054 1.02549 6.79528 1.04798 7.22267C1.07046 7.65007 1.2165 8.06175 1.46838 8.40778L2.31504 9.57111C2.61698 9.98635 2.77958 10.4866 2.77949 11C2.77958 11.5134 2.61698 12.0137 2.31504 12.4289L1.46838 13.5922C1.2165 13.9382 1.07046 14.3499 1.04798 14.7773C1.02549 15.2047 1.12753 15.6295 1.34171 16C1.55582 16.3706 1.87274 16.6712 2.25411 16.8654C2.63548 17.0596 3.06496 17.1392 3.4906 17.0944L4.9206 16.9422C5.43127 16.8886 5.94581 16.9979 6.3906 17.2544C6.83513 17.511 7.18681 17.902 7.39504 18.3711Z"
                stroke="#84919E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.99992 14C11.6568 14 12.9999 12.6569 12.9999 11C12.9999 9.34315 11.6568 8 9.99992 8C8.34307 8 6.99992 9.34315 6.99992 11C6.99992 12.6569 8.34307 14 9.99992 14Z"
                stroke="#84919E"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </IconButton>
        </div>
      </div>

      <CurrenciesModal isOpen={isModalOpen} onClose={handleCloseModal} />
      <AirdropModal isOpen={isUSDTModalOpen} onClose={handleCloseUSDTModal} />
      <RechargeModal
        isOpen={isRechargeModalOpen}
        onClose={handleCloseRechargeModal}
        onRechargeSuccess={handleRechargeSuccess}
      />
    </div>
  );
}
